/* eslint-disable react/display-name, react/prop-types */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import AuthenticatedContainer from 'components/AuthenticatedContainer';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import EVChargingTOSContent from '../Components/EVChargingTOSContent';
import EVChargingService from '../../services/EVChargingService';
import useAPI from 'useAPI';
import {
  NAV_HEIGHT,
  PAGE_TOP_PADDING,
  applyPageVisibility,
  buildPages,
  getBlockElements,
} from '../Components/paginatePolicy';

export const EVChargingTOS = () => {
  const navigate = useNavigate();
  const api = useAPI();
  const outerRef = useRef(null);
  const measureRef = useRef(null);
  const displayRef = useRef(null);
  const pagesRef = useRef([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (api) {
      EVChargingService.init(api);
    }
  }, [api]);

  const handleAgree = useCallback(async () => {
    setError(null);
    setSubmitting(true);
    try {
      await EVChargingService.acceptTos();
      navigate('/ev-charging/payment');
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Unable to record your acceptance. Please try again.',
      );
      setSubmitting(false);
    }
  }, [navigate]);

  const computePages = useCallback(() => {
    const outer = outerRef.current;
    const measureEl = measureRef.current;
    if (!outer || !measureEl) return;

    const availableHeight = outer.clientHeight - NAV_HEIGHT - PAGE_TOP_PADDING;
    if (availableHeight <= 0) return;

    const policyRoot = measureEl.querySelector('.policy');
    if (!policyRoot) return;

    const blocks = getBlockElements(policyRoot);
    if (blocks.length === 0) return;

    const newPages = buildPages(blocks, availableHeight);

    pagesRef.current = newPages;
    setTotalPages(newPages.length);
    setCurrentPage(prev => Math.min(prev, Math.max(0, newPages.length - 1)));
  }, []);

  useEffect(() => {
    if (displayRef.current && pagesRef.current.length > 0) {
      applyPageVisibility(displayRef.current, pagesRef.current[currentPage] || []);
      const needsScroll = displayRef.current.scrollHeight > displayRef.current.clientHeight;
      displayRef.current.style.overflowY = needsScroll ? 'auto' : 'hidden';
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const timer = setTimeout(computePages, 100);
    const observer = new ResizeObserver(computePages);
    if (outerRef.current) observer.observe(outerRef.current);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [computePages]);

  const isLastPage = totalPages > 0 && currentPage === totalPages - 1;

  return (
    <AuthenticatedContainer>
      <Box
        ref={outerRef}
        sx={{
          width: '100%',
          height: 'calc(100dvh - 140px)',
          maxHeight: 'calc(100dvh - 140px)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Hidden measurement container */}
        <Box
          ref={measureRef}
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            top: 0,
            left: 0,
            right: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        >
          <EVChargingTOSContent />
        </Box>

        {/* Visible display */}
        <Box ref={displayRef} sx={{ flex: 1, overflow: 'hidden', pt: `${PAGE_TOP_PADDING}px` }}>
          <EVChargingTOSContent />
        </Box>

        {/* Navigation bar */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px solid #e0e0e0',
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 40,
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                onClick={() =>
                  currentPage === 0
                    ? navigate('/ev-charging')
                    : setCurrentPage(p => p - 1)
                }
                disabled={submitting}
              >
                Back
              </Button>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {totalPages > 1 && (
                <Typography variant="body2" color="text.secondary">
                  Page {currentPage + 1} of {totalPages}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              {!isLastPage && (
                <Button onClick={() => setCurrentPage(p => p + 1)}>
                  {'Next ->'}
                </Button>
              )}
              {isLastPage && (
                <Button
                  onClick={handleAgree}
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={18} /> : 'I Agree'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </AuthenticatedContainer>
  );
};

export default EVChargingTOS;
